import { useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { useFetcher, useSearchParams } from '@remix-run/react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
	LOGIN_FORM,
	LOGIN_INPUTS,
	loginSchema,
} from '#app/components/forms/login-form/login-form.tsx'
import {
	LOGIN_I18NEXT,
	LOGIN_SEARCH_PARAMS,
} from '#app/routes/($lang)._auth.login/route-config'
import { type App } from '#app/routes/_layout/component.route.tsx'
import { ROOT_INPUTS } from '#app/routes/_layout/route-config.ts'

type UseLoginProps = {
	app: App
	setApp: React.Dispatch<React.SetStateAction<App>>
}

function useLoginForm({ app, setApp }: UseLoginProps) {
	const { t } = useTranslation(LOGIN_I18NEXT.routeAuthLogin)
	const [searchParams] = useSearchParams()

	const fetcherKey = LOGIN_FORM.fetcherKey
	const fetcher = useFetcher({ key: fetcherKey })

	const formValues = {
		[ROOT_INPUTS.subaction]: LOGIN_FORM.subaction,
		[ROOT_INPUTS.noJs]: String(app.options.noJs || true),
		...(app.options.redirect && {
			[ROOT_INPUTS.redirect]: app.options.redirect,
		}),
	}
	const [form, fields] = useForm({
		lastResult: fetcher.data || undefined,
		defaultValue: {
			[LOGIN_INPUTS.email]:
				searchParams.get(LOGIN_SEARCH_PARAMS.email) ||
				app.options.data?.[LOGIN_INPUTS.email] ||
				undefined,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: loginSchema })
		},
		shouldValidate: 'onInput',
	})

	React.useEffect(() => {
		if (fetcher.state === 'loading') {
			setApp((prev) => ({
				...prev,
				displayModal: false,
			}))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetcher.state])

	return {
		loginFetcherKey: fetcherKey,
		loginFetcher: fetcher,
		loginFormValues: formValues,
		loginForm: form,
		loginFields: fields,
		t,
		searchParams,
	}
}

export { type UseLoginProps, useLoginForm }
